import { useState } from "react";
import { Link } from "react-router-dom";
import { api, type DisplayProfile, type MonitorReport, type Station } from "../api";
import { useAuth } from "../auth";
import { AllCheck, BulkBar, RowCheck, rowKey, useSelection, type Selection } from "../components/Bulk";
import { errorText, useLoad } from "../hooks";
import { currentScreenReport, screenDetails } from "../lib/probe";

export function StationsPage() {
  const { user } = useAuth();
  const stations = useLoad(() => api.stations(), []);
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const sel = useSelection();
  const isAdmin = user?.role === "admin";
  const list = stations.data ?? [];
  const stationKeys = list.map((s) => rowKey("stations", s.id));

  const create = async () => {
    try {
      await api.createStation({ name, kind: user?.role === "patient" ? "home" : "clinic", site_id: user?.site_id ?? null });
      setName("");
      stations.reload();
    } catch (e) {
      setError(errorText(e));
    }
  };

  return (
    <>
      <h1>Stations</h1>
      <p className="muted">A station is a computer and monitor used for testing. Register each monitor once, on the monitor
        itself, then use <strong>Calibrate</strong> to save its brightness with the lux meter (its baseline).</p>
      {isAdmin && list.length > 0 && (
        <div className="card">
          <div className="row" style={{ alignItems: "center" }}>
            <label className="inline"><AllCheck sel={sel} keys={stationKeys} label="Select all stations" /> all stations</label>
          </div>
          <BulkBar sel={sel} shown={stationKeys} what="stations or displays" onDeleted={() => { stations.reload(); setTick((t) => t + 1); }}
            presets={[
              { label: "Home stations", keys: list.filter((s) => s.kind === "home").map((s) => rowKey("stations", s.id)) },
              { label: "Clinic stations", keys: list.filter((s) => s.kind !== "home").map((s) => rowKey("stations", s.id)) },
            ]} />
          <p className="small muted">A station’s displays and calibrations go with it. A station or display that sessions
            were run on is refused unless you also delete those sessions.</p>
        </div>
      )}
      {list.map((s) => <StationCard key={s.id} station={s} tick={tick} sel={isAdmin ? sel : null} order={stationKeys} />)}
      <div className="card">
        <div className="row">
          <label>New station name<input value={name} onChange={(e) => setName(e.target.value)} placeholder={user?.role === "patient" ? "Home computer" : "Clinic room 1"} /></label>
          <button disabled={!name} onClick={() => void create()}>Add station</button>
        </div>
        {error && <div className="error">{error}</div>}
      </div>
    </>
  );
}

function StationCard({ station, tick, sel, order }: { station: Station; tick: number; sel: Selection | null; order: string[] }) {
  const displays = useLoad(() => api.displays(station.id), [station.id, tick]);
  const [adding, setAdding] = useState(false);
  const k = rowKey("stations", station.id);
  const picked = !!sel?.has(k);
  const displayKeys = (displays.data ?? []).map((d) => rowKey("displays", d.id));
  return (
    <div className={`card ${picked ? "picked-card" : ""}`}>
      <h2 style={{ marginTop: 0 }}>
        {sel && <><RowCheck sel={sel} k={k} order={order} label={`Select station ${station.name}`} />{" "}</>}
        {station.name} <span className="badge">{station.kind}</span></h2>
      <table>
        <thead><tr>{sel && <th className="pick-col" />}<th>Display</th><th>Size</th><th>EDID says</th><th /></tr></thead>
        <tbody>
          {(displays.data ?? []).map((d) => <DisplayRow key={d.id} d={d} sel={sel} order={displayKeys} stationPicked={picked} />)}
        </tbody>
      </table>
      {adding ? <RegisterDisplay stationId={station.id} onDone={() => { setAdding(false); displays.reload(); }} />
        : <button className="secondary" style={{ marginTop: "0.8rem" }} onClick={() => setAdding(true)}>Register the screen this window is on</button>}
    </div>
  );
}

function DisplayRow({ d, sel, order, stationPicked }: { d: DisplayProfile; sel: Selection | null; order: string[]; stationPicked: boolean }) {
  const { user } = useAuth();
  const caps = (d.edid_parsed?.capabilities ?? null) as Record<string, unknown> | null;
  const [approving, setApproving] = useState(false);
  const k = rowKey("displays", d.id);
  return (
    <>
    <tr className={sel && (stationPicked || sel.has(k)) ? "picked" : ""}>
      {sel && <td className="pick-col"><RowCheck sel={sel} k={k} order={order} disabled={stationPicked}
        checked={stationPicked || sel.has(k)} label={`Select display ${d.label || d.id}`} /></td>}
      <td>{d.label || `display ${d.id}`}<div className="small muted">{d.browser_label}</div></td>
      <td>{d.width_px}×{d.height_px} px, {d.width_mm.toFixed(0)}×{d.height_mm.toFixed(0)} mm <span className="muted small">({d.size_source})</span></td>
      <td className="small">
        {caps ? <>
          {caps.panel_bits ? `${caps.panel_bits}-bit` : "bit depth unknown"}
          {caps.srgb_default ? ", sRGB default" : ""}{caps.hdr_pq ? ", HDR10" : ""}{caps.bt2020 ? ", BT.2020" : ""}
        </> : <span className="muted">no EDID</span>}
      </td>
      <td>
        <Link to={`/displays/${d.id}/calibrate`}>Calibrate (lux meter)</Link>
        {user?.role === "admin" && !d.model_id && (
          <> · <button className="link" onClick={() => setApproving(true)}>Approve for home use</button></>
        )}
      </td>
    </tr>
    {approving && <tr><td colSpan={sel ? 5 : 4}><ApproveModel d={d} onDone={() => setApproving(false)} /></td></tr>}
    </>
  );
}

/** Make this registered display an approved model: home screens with the same name and
 *  resolution then qualify and use its size. Nothing is measured here; each monitor gets
 *  its own lux baseline (Calibrate). */
function ApproveModel({ d, onDone }: { d: DisplayProfile; onDone: () => void }) {
  const [name, setName] = useState(d.label);
  const [pattern, setPattern] = useState(d.browser_label);
  const [instructions, setInstructions] = useState("");
  const here = /Macintosh|Mac OS X/.test(navigator.userAgent) ? "mac" : /Windows/.test(navigator.userAgent) ? "windows" : "linux";
  const [platforms, setPlatforms] = useState<string[]>([here]);
  const [msg, setMsg] = useState<string | null>(null);
  const toggle = (x: string) => setPlatforms(platforms.includes(x) ? platforms.filter((y) => y !== x) : [...platforms, x]);
  const save = async () => {
    try {
      await api.createDisplayModel({
        display_id: d.id, name, label_pattern: pattern, instructions, platforms,
      });
      setMsg("Approved. Home screens named like this, at this resolution, will now pass the screen check.");
      setTimeout(onDone, 1500);
    } catch (e) {
      setMsg(errorText(e));
    }
  };
  return (
    <div className="row">
      <label>Model name<input value={name} onChange={(e) => setName(e.target.value)} /></label>
      <label>Screen name contains<input value={pattern} onChange={(e) => setPattern(e.target.value)} placeholder="from 'Read from browser'" /></label>
      <label>Setting to tell users<input value={instructions} onChange={(e) => setInstructions(e.target.value)} placeholder="e.g. Standard mode, brightness 40, HDR off" /></label>
      <fieldset><legend>Checked with</legend>
        {[["mac", "Mac"], ["windows", "Windows"], ["linux", "Linux"], ["chromeos", "ChromeOS"]].map(([k, l]) => (
          <label key={k} style={{ flexDirection: "row", gap: "0.3rem" }}><input type="checkbox" checked={platforms.includes(k!)} onChange={() => toggle(k!)} />{l}</label>
        ))}
      </fieldset>
      <button disabled={!name || pattern.length < 2} onClick={() => void save()}>Approve</button>
      <button className="secondary" onClick={onDone}>Cancel</button>
      {msg && <div className="small">{msg}</div>}
    </div>
  );
}

function RegisterDisplay({ stationId, onDone }: { stationId: number; onDone: () => void }) {
  const scr = currentScreenReport();
  const [label, setLabel] = useState("");
  const [browserLabel, setBrowserLabel] = useState("");
  const [edid, setEdid] = useState("");
  const [preview, setPreview] = useState<Record<string, any> | null>(null);
  const [widthPx, setWidthPx] = useState(scr.widthPx);
  const [heightPx, setHeightPx] = useState(scr.heightPx);
  const builtIn = appleBuiltInPanel(scr.widthPx, scr.heightPx);
  const [sizeSource, setSizeSource] = useState<"edid" | "ruler" | "card" | "spec">("edid");
  const [widthMm, setWidthMm] = useState("");
  const [heightMm, setHeightMm] = useState("");
  const [cardCss, setCardCss] = useState(320);
  const [error, setError] = useState<string | null>(null);

  const readFile = async (f: File) => {
    setError(null);
    setPreview(null);
    const buf = new Uint8Array(await f.arrayBuffer());
    if (buf.length === 0) {
      setEdid("");
      setError(`“${f.name}” is empty: the command that made it found no EDID. A Mac's built-in screen has none; ` +
        "for a monitor, use Check my monitor below, or register the screen by its size.");
      return;
    }
    const isBinary = buf[0] === 0x00;
    if (isBinary && !(buf[1] === 0xff && buf[7] === 0x00)) {
      setEdid("");
      setError(`“${f.name}” is not an EDID (it does not start with the EDID header 00 ff ff ff ff ff ff 00).`);
      return;
    }
    let bin = "";
    buf.forEach((b) => { bin += String.fromCharCode(b); });
    const text = isBinary ? btoa(bin) : new TextDecoder().decode(buf);
    setEdid(text);
    await doPreview(text);
  };

  const doPreview = async (text = edid) => {
    setError(null);
    try {
      const p = await api.parseEdid(text.trim());
      setPreview(p);
      if (!label && (p.name || p.capabilities?.name)) setLabel(p.name || p.capabilities.name);
    } catch (e) {
      setError(errorText(e));
      setPreview(null);
    }
  };

  const useHelperMonitor = async (m: Record<string, any>) => {
    if (m.b64) {
      setEdid(m.b64);
      await doPreview(m.b64);
    } else {
      setError("This monitor's raw EDID was not available on this computer, only its name and maker. " +
        "Register it by size below.");
    }
    if (!label) setLabel([m.manufacturer, m.capabilities?.name].filter(Boolean).join(" "));
  };

  const readLabel = async () => {
    const d = await screenDetails();
    const cur = d?.current as { label?: string; isInternal?: boolean } | undefined;
    if (cur) setBrowserLabel(cur.label ?? "");
    // A Mac's built-in screen has no EDID: take its size from Apple's published density.
    if (cur?.isInternal && builtIn) {
      setSizeSource("spec");
      if (!label) setLabel(builtIn.name);
    }
  };

  const cardPxPerMm = (cardCss * window.devicePixelRatio) / 85.6;
  const submit = async () => {
    setError(null);
    let w: number | null = null;
    let h: number | null = null;
    if (sizeSource === "ruler") { w = Number(widthMm); h = Number(heightMm); }
    if (sizeSource === "card") { w = widthPx / cardPxPerMm; h = heightPx / cardPxPerMm; }
    if (sizeSource === "spec") {
      const panel = appleBuiltInPanel(widthPx, heightPx);
      if (!panel) { setError("Apple's published size is known only for MacBook Pro and Air built-in screens at their native resolution."); return; }
      w = Math.round((widthPx / panel.ppi) * 254) / 10;
      h = Math.round((heightPx / panel.ppi) * 254) / 10;
    }
    try {
      await api.addDisplay(stationId, {
        label, edid: edid.trim() || null, width_px: widthPx, height_px: heightPx,
        width_mm: w, height_mm: h, size_source: sizeSource, browser_label: browserLabel,
      });
      onDone();
    } catch (e) {
      setError(errorText(e));
    }
  };

  const caps = preview?.capabilities as Record<string, any> | undefined;
  const spec = appleBuiltInPanel(widthPx, heightPx);
  return (
    <div style={{ marginTop: "1rem", display: "grid", gap: "0.8rem" }}>
      <div className="row">
        <label>Label<input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. MacBook Pro built-in" /></label>
        <label>Browser screen label<input value={browserLabel} onChange={(e) => setBrowserLabel(e.target.value)} /></label>
        <button className="secondary" onClick={() => void readLabel()}>Read from browser</button>
      </div>
      <ReadWithHelper onUse={(m) => void useHelperMonitor(m)} />
      <label>EDID (upload an EDID file, or paste hex, base64 or the Terminal output below)
        <input type="file" accept=".bin,.edid,.txt,.json" onChange={(e) => e.target.files?.[0] && void readFile(e.target.files[0])} />
        <textarea value={edid} onChange={(e) => setEdid(e.target.value)} placeholder="00 ff ff ff ff ff ff 00 …" />
      </label>
      <details className="small">
        <summary>Or copy it with the Mac's Terminal</summary>
        <p>Paste this into Terminal and press Return. It copies every monitor EDID the Mac reports; then paste here (⌘V).</p>
        <pre className="cmd">{MAC_EDID_COMMAND}</pre>
        <p className="muted">Nothing pasted? The Mac reported no raw EDID. That is normal for the built-in screen; register it by size.</p>
      </details>
      <div><button className="secondary" disabled={!edid.trim()} onClick={() => void doPreview()}>Read EDID</button></div>
      {preview && preview.edids_found > 1 && (
        <p className="small warn">{preview.edids_found} EDIDs were pasted; the first is used. To register another monitor, paste only its line.</p>
      )}
      {caps && (
        <div className="small">
          <strong>{preview?.manufacturer} {caps.name ?? ""}</strong> — {caps.panel_bits ? `${caps.panel_bits}-bit panel` : "bit depth not stated"},
          {" "}{caps.srgb_default ? "sRGB default" : "not sRGB default"}, {caps.hdr_pq ? "HDR10 (PQ) capable" : "no HDR10"},
          {" "}size {caps.physical_size_mm ? `${caps.physical_size_mm[0]}×${caps.physical_size_mm[1]} mm` : "unknown"}.
          <div className="muted">EDID is what the monitor claims; the browser’s colour depth shows what Chrome sends it.</div>
        </div>
      )}
      <div className="row">
        <label>Width (device px)<input type="number" value={widthPx} onChange={(e) => setWidthPx(Number(e.target.value))} /></label>
        <label>Height (device px)<input type="number" value={heightPx} onChange={(e) => setHeightPx(Number(e.target.value))} /></label>
        <label>Physical size from
          <select aria-label="Physical size from" value={sizeSource} onChange={(e) => setSizeSource(e.target.value as typeof sizeSource)}>
            <option value="edid">EDID</option>
            <option value="ruler">Ruler (enter mm)</option>
            <option value="card">Credit card match</option>
            <option value="spec">Apple’s published size (MacBook built-in screen)</option>
          </select>
        </label>
        {sizeSource === "spec" && (
          <span className="small muted" style={{ alignSelf: "flex-end" }}>
            {spec
              ? `${spec.name}: ${spec.ppi} ppi → ${(widthPx / spec.ppi * 25.4).toFixed(1)} × ${(heightPx / spec.ppi * 25.4).toFixed(1)} mm`
              : "not a known built-in screen at this resolution"}
          </span>
        )}
        {sizeSource === "ruler" && <>
          <label>Visible width (mm)<input type="number" value={widthMm} onChange={(e) => setWidthMm(e.target.value)} /></label>
          <label>Visible height (mm)<input type="number" value={heightMm} onChange={(e) => setHeightMm(e.target.value)} /></label>
        </>}
      </div>
      {sizeSource === "card" && (
        <div>
          <p className="small">Hold a bank card (85.6 mm wide) against the screen and drag until the box matches its width.</p>
          <div style={{ width: cardCss, height: cardCss * 53.98 / 85.6, border: "2px solid #2a6fdb", borderRadius: 8 }} />
          <input type="range" min={150} max={700} step={0.5} value={cardCss} onChange={(e) => setCardCss(Number(e.target.value))} style={{ width: 400 }} />
          <div className="small muted">{cardPxPerMm.toFixed(3)} px/mm → screen {(widthPx / cardPxPerMm).toFixed(0)}×{(heightPx / cardPxPerMm).toFixed(0)} mm</div>
        </div>
      )}
      <p className="small muted">This window is on a screen of {scr.widthPx}×{scr.heightPx} device pixels (colour depth reported: {scr.colorDepth}).</p>
      {error && <div className="error">{error}</div>}
      <div><button onClick={() => void submit()}>Save display</button></div>
    </div>
  );
}

/** Built-in Mac screens by native resolution (device px), from Apple's technical
 *  specifications. They have no EDID, so this is how their physical size is known. */
const APPLE_PANELS: Record<string, { name: string; ppi: number }> = {
  "3024x1964": { name: "MacBook Pro 14-inch built-in (Liquid Retina XDR)", ppi: 254 },
  "3456x2234": { name: "MacBook Pro 16-inch built-in (Liquid Retina XDR)", ppi: 254 },
  "2560x1664": { name: "MacBook Air 13-inch built-in (Liquid Retina)", ppi: 224 },
  "2880x1864": { name: "MacBook Air 15-inch built-in (Liquid Retina)", ppi: 224 },
};

export function appleBuiltInPanel(widthPx: number, heightPx: number): { name: string; ppi: number } | null {
  return APPLE_PANELS[`${widthPx}x${heightPx}`] ?? null;
}

/** Every raw EDID ioreg prints (any property name, Intel or Apple silicon), to the clipboard. */
export const MAC_EDID_COMMAND = "ioreg -l -w0 | grep -oE '<00ffffffffffff00[0-9a-fA-F]+>' | pbcopy";

/** The same "Check my monitor" helper patients use, run by staff on the station computer;
 *  its report (with the raw EDIDs) fills the form. */
function ReadWithHelper({ onUse }: { onUse: (m: Record<string, any>) => void }) {
  const [open, setOpen] = useState(false);
  const [report, setReport] = useState<MonitorReport | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const fetchReport = async () => {
    setError(null);
    try {
      setReport((await api.myMonitorEdids()).report);
    } catch (e) {
      setError(errorText(e));
    }
  };
  if (!open) {
    return <div><button className="secondary" onClick={() => setOpen(true)}>Read the EDID with Check my monitor</button></div>;
  }
  return (
    <div className="card" style={{ margin: 0 }}>
      <p style={{ marginTop: 0 }}>On this computer, with the monitor connected:</p>
      <ol className="small">
        <li>Download and open the helper:{" "}
          <a href={api.monitorHelperUrl("mac")}>Mac</a> · <a href={api.monitorHelperUrl("windows")}>Windows</a>.
          On a Mac the first run stops at “Not Opened”, which offers only Move to Trash or Done: click Done, then
          System Settings → Privacy &amp; Security → Open Anyway, and confirm with Open Anyway and the Mac password.</li>
        <li>When it says “Done”, press the button below.</li>
      </ol>
      <button onClick={() => void fetchReport()}>Use what it found</button>
      {report === null && <p className="small warn">No report yet. Each download works once, for 30 minutes.</p>}
      {report && (
        <ul className="small">
          {report.monitors.length === 0 && <li>It found no monitor details on this computer.</li>}
          {report.monitors.map((m, i) => (
            <li key={i}>
              {[m.manufacturer, m.capabilities?.name].filter(Boolean).join(" ") || "unnamed monitor"}
              {m.serial_text || m.serial_number ? `, serial ${m.serial_text || m.serial_number}` : ""}
              {m.b64 ? "" : " (name only, no EDID)"}{" "}
              <button className="link" onClick={() => onUse(m)}>Use this one</button>
            </li>
          ))}
          <li className="muted">read {new Date(report.created_at).toLocaleString()}</li>
        </ul>
      )}
      {error && <div className="error">{error}</div>}
    </div>
  );
}
